// src/components/organisms/Modals/EditAccountModal/index.tsx
import styles from "../AddAccountModal/styles.module.css";
import { closeModal } from "src/store/slices/modalSlice";
import { useSelector } from "react-redux";
import { Button, BUTTONTYPE } from "src/components/atoms/Button";
import closeIcon from "../../../../assets/icons/closeGray.svg";
import { useFormik } from "src/hooks/useFormik";
import * as Yup from "yup";
import { Select } from "src/components/atoms/Select";
import { Input } from "src/components/atoms/Input";
import { useMemo, useState } from "react";
import { itemsType, itemsCurrency } from "../AddAccountModal/data";
import useBanks from "src/hooks/useBanks";
import { useClientCompanies } from "src/hooks/useClientCompanies";
import { useAppDispatch } from "src/store/hooks";
import { fetchClientAccounts } from "src/store/slices/clientDetailSlice";
import { apiClient } from "src/api/client";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";

type Option = {
  id: string | number;
  label: string;
};

const validationSchema = Yup.object({
  accountName: Yup.string()
    .required("Account name is required")
    .min(2, "Account name must be at least 2 characters"),
  accountType: Yup.object().required("Account type is required"),
});

export const EditAccountModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modalData = useSelector(
    (state: any) =>
      (state.modal.data["editAccountModal"] as {
        accountId: number;
        clientId: number;
        accountName: string;
        accountType: string;
        bankId: number;
        bankName: string;
        companyId: number;
        companyName: string;
        currency: string;
      }) || {}
  );

  const { banks, loading: banksLoading } = useBanks();
  const { companies, loading: companiesLoading } = useClientCompanies();

  const handleClose = () => {
    dispatch(closeModal({ type: "editAccountModal" }));
  };

  // Находим начальный тип аккаунта
  const initialAccountType = useMemo(() => {
    if (!modalData.accountType) return null;
    return (
      itemsType.find(
        (t) => t.label.toLowerCase() === modalData.accountType.toLowerCase()
      ) ?? null
    );
  }, [modalData.accountType]);

  // Находим начальную валюту
  const initialCurrency = useMemo(() => {
    if (!modalData.currency) return null;
    return (
      itemsCurrency.find(
        (c) => c.label.toLowerCase() === modalData.currency.toLowerCase()
      ) ?? null
    );
  }, [modalData.currency]);

  // Находим начальный банк
  const itemsBank = useMemo(
    () => banks.map((bank) => ({ id: bank.id, label: bank.name })),
    [banks]
  );

  const initialBank = useMemo(() => {
    if (!modalData.bankId) return null;
    return itemsBank.find((b) => b.id === modalData.bankId) ?? null;
  }, [modalData.bankId, itemsBank]);

  // Находим начальную компанию
  const itemsCompany = useMemo(
    () => companies.map((company) => ({ id: company.id, label: company.name })),
    [companies]
  );

  const initialCompany = useMemo(() => {
    if (!modalData.companyId) return null;
    return itemsCompany.find((c) => c.id === modalData.companyId) ?? null;
  }, [modalData.companyId, itemsCompany]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      accountName: modalData.accountName || "",
      accountType: initialAccountType as Option | null,
      accountBank: initialBank as Option | null,
      accountCompany: initialCompany as Option | null,
      accountCurrency: initialCurrency as Option | null,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setError(null);
      try {
        await apiClient.updateAccount(modalData.accountId, {
          account_name: values.accountName.trim(),
          account_type: values.accountType?.label?.toLowerCase() || "checking",
        });

        if (modalData.clientId) {
          dispatch(fetchClientAccounts({ client_id: modalData.clientId }));
        }

        window.dispatchEvent(new CustomEvent("accountUpdated"));
        handleClose();
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const getError = (touched: boolean | undefined, err: any): string | undefined =>
    touched && err ? (err as string) : undefined;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h3>Edit account</h3>
          <Button
            text=""
            icon={closeIcon}
            btnType={BUTTONTYPE.WITHOUT_BORDER}
            onClick={handleClose}
            disabled={isSubmitting}
          />
        </div>

        <form onSubmit={formik.handleSubmit} className={styles.form}>
          {error && <ErrorBlock error={error} />}

          <Input
            value={formik.values.accountName}
            label="Account name"
            onChange={formik.handleChange}
            name="accountName"
            error={formik.touched.accountName && formik.errors.accountName}
            disabled={isSubmitting}
          />

          <Select
            label="Account bank"
            items={itemsBank}
            onSelect={() => {}}
            selectedId={formik.values.accountBank?.id}
            // GET /banks не отдаёт банки без скриптов (docs/plaid-api-contract.md) — для Plaid-счёта
            // банк не найдётся в itemsBank, и Select без совпадения показывает placeholder: отдаём в
            // него название банка, чтобы не показывать пустое "Select bank".
            placeholder={banksLoading ? "Loading banks..." : modalData.bankName || "Select bank"}
            disabled={true}
          />

          <Select
            label="Account company"
            items={itemsCompany}
            onSelect={() => {}}
            selectedId={formik.values.accountCompany?.id}
            placeholder={companiesLoading ? "Loading companies..." : "Select company"}
            disabled={true}
          />

          <Select
            label="Account type"
            items={itemsType}
            onSelect={() => {}}
            selectedId={formik.values.accountType?.id}
            disabled={true}
            error={getError(formik.touched.accountType, formik.errors.accountType)}
          />

          <Select
            label="Account currency"
            items={itemsCurrency}
            onSelect={() => {}}
            selectedId={formik.values.accountCurrency?.id}
            disabled={true}
          />

          <Button
            text={isSubmitting ? "Saving..." : "Save changes"}
            type="submit"
            disabled={!formik.isValid || isSubmitting}
          />
        </form>
      </div>
    </div>
  );
};
